import { BaseTextFieldProps, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import * as z from 'zod';

export interface IMaskedPhoneNumberInputProps extends BaseTextFieldProps {
	name: string;
	label?: string;
}

type ValueOf<T> = T[keyof T];

export const MaskedPhoneNumberInputSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	// validations:
	default: z.coerce.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Valid Phone Number Required'),
});

export const formatPhoneNumber = (val: string) => {
	const digits = val.replace(/[^\d]/g, '');
	if (digits.length < 1) {
		return '';
	}
	if (digits.length < 4) {
		return `(${digits}`;
	}
	if (digits.length < 7) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	}
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export default function MaskedPhoneNumberInput({ label, name, ...props }: IMaskedPhoneNumberInputProps) {
	const [initialFormatting, setInitialFormatting] = useState(false);
	const { control, setValue } = useFormContext();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = errors[name];
	field.value = formatPhoneNumber(field.value);

	useEffect(() => {
		if (!initialFormatting) {
			setValue(name, formatPhoneNumber(field.value));
			setInitialFormatting(true);
		}
	}, [field.value, initialFormatting, name, setValue]);

	return (
		<TextField
			label={label}
			error={Boolean(error)}
			helperText={(error?.message as string) ?? props.helperText}
			placeholder="(XXX) XXX-XXXX"
			{...field}
			onChange={(e) => {
				const formatted = formatPhoneNumber(e.target.value);
				field.onChange(formatted);
			}}
			required
			{...props}
		/>
	);
}
